import React, { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";
import z from "zod";

import {
  maritalStatusEnumSchema,
  MaritalStatusEnumType,
} from "@libs/api/patients";
import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnExpandableCard } from "@/components/VnExpandableCard";
import { VnForm } from "@/components/VnForm";
import { VnRadioGroupInput } from "@/components/VnRadioGroupInput/VnRadioGroupInput";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  householdMemberMaritalStatus,
  HouseholdMemberType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { i18n } from "@/i18n";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { formatHouseholdMemberName } from "@/utils/householdMember";

type MemberMaritalStatusFormPropsType = {
  member: HouseholdMemberType;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
};

type MemberMaritalStatusFormType = Pick<HouseholdMemberType, "maritalStatus">;

const memberMaritalStatusFormSchema = z.object({
  maritalStatus: z.enum(maritalStatusEnumSchema.options, {
    message: i18n.t("common:errors.selectValidOption"),
  }),
});

const MemberMaritalStatusForm: React.FC<MemberMaritalStatusFormPropsType> = ({
  member,
  isExpanded,
  onToggleExpand,
  onClose,
}) => {
  const { t } = useTranslator();
  const maritalStatus = member.maritalStatus ?? null;
  const form = useForm<MemberMaritalStatusFormType>({
    defaultValues: {
      maritalStatus,
    },
    resolver: standardSchemaResolver(memberMaritalStatusFormSchema),
  });

  const [shouldShowEditButton, setShouldShowEditButton] = useState(
    Boolean(maritalStatus),
  );

  const { mutateAsync: updateHouseholdMember, isPending } =
    useUpdateHouseholdMemberMutation();

  const onSave = () => {
    void form.handleSubmit(handleSave)();
  };

  const handleSave = async (formData: MemberMaritalStatusFormType) => {
    await updateHouseholdMember(
      {
        id: member.id,
        householdMemberData: {
          maritalStatus: formData.maritalStatus,
        },
      },
      {
        onSuccess: () => {
          setShouldShowEditButton(true);
          onToggleExpand();
        },
      },
    );
  };

  const onCancel = () => {
    form.reset({
      maritalStatus: member.maritalStatus ?? null,
    });
    onClose();
  };

  const formatMaritalStatus = (status: MaritalStatusEnumType | null) => {
    if (!status)
      return t(`native:applicationMaritalStatusScreen.needsMaritalStatus`);
    return t(`common:maritalStatus.${status}`);
  };

  return (
    <VnForm form={form} isInputOnlyForm>
      <VnExpandableCard
        title={formatHouseholdMemberName(member)}
        subtitle={formatMaritalStatus(maritalStatus)}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onSave={onSave}
        onCancel={onCancel}
        isLoading={isPending}
        shouldShowEditButton={shouldShowEditButton}>
        <VnRadioGroupInput
          name="maritalStatus"
          control={form.control}
          options={householdMemberMaritalStatus}
          radioGroupClassName="gap-4 bg-background p-4 rounded-md"
        />
      </VnExpandableCard>
    </VnForm>
  );
};

export const ApplicationMaritalStatusScreen = () => {
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const handleToggleExpand = (memberId: string) => {
    setExpandedMemberId(prev => (prev === memberId ? null : memberId));
  };

  const handleClose = () => {
    setExpandedMemberId(null);
  };

  const isNextDisabled = useMemo(() => {
    if (!householdMembers) return true;
    return householdMembers.some(member => !member.maritalStatus);
  }, [householdMembers]);

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "MARITAL_STATUS",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/child-caregiver-status",
          });
        },
      },
    );
  };

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      isNextButtonDisabled={isNextDisabled}
      titleI18nKey="native:applicationMaritalStatusScreen.title"
      descriptionI18nKey="native:applicationMaritalStatusScreen.description">
      <VnBox className="gap-4">
        {householdMembers?.map(member => {
          const isExpanded = expandedMemberId === member.id;
          return (
            <MemberMaritalStatusForm
              key={member.id}
              member={member}
              isExpanded={isExpanded}
              onToggleExpand={() => handleToggleExpand(member.id)}
              onClose={handleClose}
            />
          );
        })}
      </VnBox>
    </ApplicationScreenLayout>
  );
};
